import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { splitOrderItemRows } from "@/lib/couponItems";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ArrowLeft,
  Download,
  FileText,
  Building2,
  Package,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileBarChart,
  Receipt,
  Home,
  RefreshCw,
  MessageSquare,
  TruckIcon,
  Activity,
} from "lucide-react";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/formatters";
import {
  getInstallmentsByOrder,
  type Installment,
} from "@/lib/mock-data/mockFinancialData";

import { format, isBefore, parseISO, subMonths, addMonths } from "date-fns";
import { cn, mapEtapaToSegment, transformScheduledToOrder } from "@/lib/utils";
import { resolveCycleYearFromCode } from "@/lib/orderCycle";
import { ordersApi, scheduledOrdersApi, Order, RawBoletoTransaction, Parcelas, InvoiceStatus, Boletos, boletosApi, productsApi } from "@/lib/api";
// Removed LoadingState
// import { LoadingState } from "./components/LoadingState";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { useBoletoViewer } from "@/hooks/useBoletoViewer";
import { toast } from "@/components/ui/sonner";
import { mapEtapaToGradeName } from "@/lib/utils";

import { OrderDetailsSkeleton } from "./component/OrderDetailsSkeleton";
import { OrderCancellationSection } from "./component/OrderCancellation/OrderCancellationSection";
import { OrderInvoices, type OrderInvoice } from "@/components/shared/OrderInvoices";
import { getItemRequestBlockedReason } from "./component/OrderSupportRequest/supportRequest";
import { OrderAddresses } from "./component/OrderAddresses";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { useAuth } from "react-oidc-context";
import StateBadge, { getStatusLabel } from "@/components/ui/stateBadge";

// Mesmo visual do título de "Ajuda com o meu pedido" (OrderCancellationSection).
const SECTION_TITLE_CLASS = "flex items-center gap-3.5 text-[19px] font-bold leading-tight tracking-normal";
const SECTION_TITLE_ICON_CLASS = "h-5 w-5 shrink-0 text-muted-foreground";

const OrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [statusOrder, setStatusOrder] = useState('Criado');
  // Seção de boletos desta tela está comentada mais abaixo; o fluxo fica pronto para quando ela voltar.
  const { openBoleto: handleGenerateBoleto } = useBoletoViewer();

  const hoje = useMemo(() => new Date(), []);
  const dateRange = useMemo(() => ({
    from: format(subMonths(hoje, 12), 'yyyy-MM-dd'),
    to: format(addMonths(hoje, 12), 'yyyy-MM-dd')
  }), [hoje]);

  // O 'role' vem de uma claim específica do OIDC/Azure AD.
  const role = user.profile?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['orderDetails', orderId, location.state?.typePedido],
    queryFn: async () => {
      if (!orderId) return null;

      if (location.state?.typePedido === 'PROGRAMADO') {
        const responseScheduledOrder = await scheduledOrdersApi.getById(orderId);
        const dto = transformScheduledToOrder(responseScheduledOrder);
        dto.quantityInstallment = 6;
        return dto;
      }

      const response = await ordersApi.getById(orderId);
      return response;
    },
    enabled: !!orderId,
    staleTime: 0,
    gcTime: 0,
  });

  const { data: orderStatus, isLoading: isLoadingOrderStatus } = useQuery({
    queryKey: ['orderStatus', order?.orderNumber, location.state?.typePedido],
    queryFn: async () => {
      if (!order) return null;

      if (location.state?.typePedido === 'PROGRAMADO') {
        setStatusOrder('confirmed');
        return {
          lastStatus: { portalB2bOrderStatus: 'confirmed' },
          statusHistory: []
        };
      }

      if (!order.orderNumber) return null;

      const response = await ordersApi.getStatus(order.orderNumber);
      const raw = order?.portalB2BOrderStatus?.toLowerCase();
      const isCreated = raw === 'pending' || raw === 'created';

      setStatusOrder(isCreated ? 'Criado' : order?.portalB2BOrderStatus || response?.lastStatus?.portalB2bOrderStatus);

      return response;
    },
    enabled: !!order,
    staleTime: 0,
    gcTime: 0,
  });


  const {
    data: invoiceStatusOV,
    isLoading: isLoadingOV,
  } = useQuery<InvoiceStatus | null>({
    queryKey: ['invoiceStatusOV', order?.orderNumber],
    queryFn: async () => {
      const response = await ordersApi.invoiceStatus(order.orderNumber);
      return response || null;
    },
    enabled: !!order,
    staleTime: 0,
    gcTime: 0,
  });

  const {
    data: transactions,
    isLoading: isLoadingBoleto,
  } = useQuery<RawBoletoTransaction[]>({
    queryKey: ['boletosTransactions', order?.customer?.clientCnpj],
    queryFn: async () => {
      if (!order?.customer?.clientCnpj) return [];
      const response = await boletosApi.get(order.customer.clientCnpj, dateRange);
      return response.data.customerTransactionsList || [];
    },
    enabled: !!order,
    staleTime: 0,
    gcTime: 0,
  });


  const getOV = useMemo(() => {
    if (!invoiceStatusOV?.invoiceList || !transactions) return [];

    return invoiceStatusOV.invoiceList.map(invoice => {
      // Encontrar todas as transações que possuem uma linha correspondente a este invoiceNumber
      const relatedTransactions = transactions.filter(trans =>
        trans.lines.some(line => line.invoiceId === invoice.invoiceNumber)
      );

      // Converter transações para objetos Parcelas
      const relatedBoletos: Parcelas[] = relatedTransactions.map(trans => ({
        parmJournalTransRecId: trans.parmJournalTransRecId,
        recIdBoleto: trans.RECID_BOLETO ?? null,
        monthYear: `Boleto ${trans.monthYear}`,
        dueDate: trans.dueDate,
        oldAmount: trans.oldAmount,
        newAmount: trans.newAmount > 0 ? trans.newAmount : trans.oldAmount,
        statusLabel: trans.paymentStatus === 2 ? 'Pago' : (trans.paymentStatus === 1 || isBefore(parseISO(trans.dueDate), hoje) ? 'Vencido' : 'Em Aberto'),
        hasBoleto: true,
        cnpjCpf: trans.cnpjCpf,
        paymentStatus: trans.paymentStatus,
        hasCorrection: trans.hasCorrection,
        parmBltAceite: trans.parmBltAceite || "",
        parmBltAlunoNome: trans.parmBltAlunoNome || "",
        parmBltAtualizouHtml: trans.parmBltAtualizouHtml || 0,
        parmBltBankCode: trans.parmBltBankCode || "",
        parmBltCedenteCnpjCpf: trans.parmBltCedenteCnpjCpf || "",
        parmBltCedenteCodigo: trans.parmBltCedenteCodigo || "",
        parmBltCedenteNome: trans.parmBltCedenteNome || "",
        parmBltCodBarras: trans.parmBltCodBarras || "",
        parmBltCodigoDv: trans.parmBltCodigoDv || "",
        parmBltCodigoOcorrencia: trans.parmBltCodigoOcorrencia || "",
        parmBltCodigoTransmissao: trans.parmBltCodigoTransmissao || "",
        parmBltContaBancariaAgencia: trans.parmBltContaBancariaAgencia || "",
        parmBltContaBancariaCarteiraPadrao: trans.parmBltContaBancariaCarteiraPadrao || "",
        parmBltContaBancariaConta: trans.parmBltContaBancariaConta || "",
        parmBltContaBancariaDigitoAgencia: trans.parmBltContaBancariaDigitoAgencia || "",
        parmBltContaBancariaDigitoConta: trans.parmBltContaBancariaDigitoConta || "",
        parmBltContaBancariaMensagemFixaTopoBoleto: trans.parmBltContaBancariaMensagemFixaTopoBoleto || "",
        parmBltContaBancariaOperacaoConta: trans.parmBltContaBancariaOperacaoConta || "",
        parmBltContaBancariaTipoCarteiraPadrao: trans.parmBltContaBancariaTipoCarteiraPadrao || 0,
        parmBltContaBancariaTipoDocumento: trans.parmBltContaBancariaTipoDocumento || 0,
        parmBltContaBancariaTipoFormaCadastramento: trans.parmBltContaBancariaTipoFormaCadastramento || 0,
        parmBltContaBancariaTipoImpressaoBoleto: trans.parmBltContaBancariaTipoImpressaoBoleto || 0,
        parmBltContaBancariaVariacaoCarteiraPadrao: trans.parmBltContaBancariaVariacaoCarteiraPadrao || "",
        parmBltDataEmissao: trans.parmBltDataEmissao || "",
        parmBltDataVencimento: trans.parmBltDataVencimento || "",
        parmBltDescricaoOcorrencia: trans.parmBltDescricaoOcorrencia || "",
        parmBltDetalheDaFatura: trans.parmBltDetalheDaFatura || "",
        parmBltEnderecoLogradouroBairro: trans.parmBltEnderecoLogradouroBairro || "",
        parmBltEnderecoLogradouroCep: trans.parmBltEnderecoLogradouroCep || "",
        parmBltEnderecoLogradouroCidade: trans.parmBltEnderecoLogradouroCidade || "",
        parmBltEnderecoLogradouroComplemento: trans.parmBltEnderecoLogradouroComplemento || "",
        parmBltEnderecoLogradouroEndereco: trans.parmBltEnderecoLogradouroEndereco || "",
        parmBltEnderecoLogradouroNumero: trans.parmBltEnderecoLogradouroNumero || "",
        parmBltEnderecoLogradouroUf: trans.parmBltEnderecoLogradouroUf || "",
        parmBltEnderecoSacadoBairro: trans.parmBltEnderecoSacadoBairro || "",
        parmBltEnderecoSacadoCep: trans.parmBltEnderecoSacadoCep || "",
        parmBltEnderecoSacadoCidade: trans.parmBltEnderecoSacadoCidade || "",
        parmBltEnderecoSacadoComplemento: trans.parmBltEnderecoSacadoComplemento || "",
        parmBltEnderecoSacadoEndereco: trans.parmBltEnderecoSacadoEndereco || "",
        parmBltEnderecoSacadoNumero: trans.parmBltEnderecoSacadoNumero || "",
        parmBltEnderecoSacadoUf: trans.parmBltEnderecoSacadoUf || "",
        parmBltEspecieDocumento: trans.parmBltEspecieDocumento || 0,
        parmBltGmPacoteDeCursoDescricao: trans.parmBltGmPacoteDeCursoDescricao || "",
        parmBltGmTurnoNome: trans.parmBltGmTurnoNome || "",
        parmBltGrupoDemonstrativo: trans.parmBltGrupoDemonstrativo || "",
        parmBltMensagemInformacaoAdicional: trans.parmBltMensagemInformacaoAdicional || "",
        parmBltMensagemInstrucoesCaixa: trans.parmBltMensagemInstrucoesCaixa || "",
        parmBltMsgReferenciaPedido: trans.parmBltMsgReferenciaPedido || "",
        parmBltNumeroControleParticipante: trans.parmBltNumeroControleParticipante || "",
        parmBltNumeroDocumento: trans.parmBltNumeroDocumento || "",
        parmBltSacadoCnpjCpf: trans.parmBltSacadoCnpjCpf || "",
        parmBltSacadoNome: trans.parmBltSacadoNome || "",
        parmBltValorTitulo: trans.parmBltValorTitulo || 0,
        linhaDigitavel: trans.parmBltCodBarras || "",
        lines: []
      }));

      return {
        ...invoice,
        boleto: relatedBoletos
      };
    });
  }, [transactions, invoiceStatusOV, hoje])

  const orderInvoices = useMemo<OrderInvoice[]>(() => getOV.map((invoice) => ({
    invoiceNumber: invoice.invoiceNumber,
    status: invoice.lastStatus?.portalB2bInvoiceStatus || 'Em processamento',
    statusDate: invoice.lastStatus?.createdAt || new Date().toISOString(),
  })), [getOV]);

  const installments = useMemo(() => {
    if (!order) return [];
    // O 'id' do pedido da API é usado para buscar as parcelas mocadas.
    // Em um cenário real, as parcelas viriam da API junto com o pedido ou de um endpoint dedicado.
    return getInstallmentsByOrder(order.id!).sort((a, b) =>
      a.installmentNumber - b.installmentNumber
    );
  }, [order]);

  const financialSummary = useMemo(() => {
    if (!order) return { totalAmount: 0, totalPaid: 0, totalPending: 0 };

    const totalPaid = installments
      .filter(i => i.status === "paid")
      .reduce((sum, i) => sum + i.amount, 0);

    const totalPending = installments
      .filter(i => i.status === "pending" || i.status === "overdue")
      .reduce((sum, i) => sum + i.amount, 0);

    return {
      totalAmount: order.totalAmount || 0,
      totalPaid,
      totalPending,
    };
  }, [installments, order]);


  const pageConf = (() => {
    const isBackoffice = window.location.pathname.includes('backoffice');
    return {
      isBackoffice,
      home: isBackoffice ? '/backoffice' : '/customer-portal',
      toBack: `/${isBackoffice ? 'backoffice' : 'customer-portal'}/pedidos`,
      // Backoffice não tem tela própria de "Realizar Pedido" — direciona sempre pro portal do cliente.
      newOrder: '/customer-portal/realizar-pedido',
    }
  })();

  const handleGoBack = () => window.history.back();

  // const handleDownloadBoleto = ( installment: Installment ) => {
  //   console.log( "Baixando boleto:", installment.boletoUrl );
  // };

  // const handleDownloadProof = ( installment: Installment ) => {
  //   console.log( "Baixando comprovante:", installment.paymentProofUrl );
  // };


  // const getInstallmentStatusBadge = ( status: Installment[ "status" ] ) => {
  //   const variants: Record<Installment[ "status" ], { variant: "outline" | "default" | "destructive"; label: string }> = {
  //     pending: { variant: "outline", label: "Pendente" },
  //     paid: { variant: "default", label: "Pago" },
  //     overdue: { variant: "destructive", label: "Vencido" },
  //   };
  //   return <Badge variant={variants[ status ].variant}>{variants[ status ].label}</Badge>;
  // };

  const hasOverdueInstallments = (installments: Installment[]) => {
    return installments.some(i => i.status === "overdue");
  };

  const [downloadPDF, setDownloadPDF] = useState<boolean>(false);
  const { regeneratePDF } = useRegeneratePDF();

  // Simular download de PDF
  const handleDownloadPDF = async () => {
    try {
      setDownloadPDF(true);
      if (!order) {
        toast.error('Erro', { description: "Pedido não encontrado" });
        return;
      }

      await regeneratePDF(order);
    } finally {
      setDownloadPDF(false);
    }
  };

  const { data: products } = useQuery({
    queryKey: ["products", order?.cycleCode],
    queryFn: async () => {
      const products = await productsApi.getActive(resolveCycleYearFromCode(order?.cycleCode));
      return products.data || [];
    }
  });

  const orderDetailsQueryKey = ['orderDetails', orderId, location.state?.typePedido] as const;

  useEffect(() => {
    if (order?.cancelledAt) {
      setStatusOrder('Cancelado');
    }
  }, [order?.cancelledAt, orderStatus]);

  const getTypeDelivery = useCallback(({ productCode, productSku }: { productCode: string; productSku: string }) => {
    if (!products) return "-";
    const product = products.find(row => row.codigoProduto == productCode || row.sku == productSku);
    return product?.utilizacao || "-";
  }, [products]);


  const sortedOrderItems = useMemo(() => {
    if (!order?.items) return [];

    const segmentOrder: Record<string, number> = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      "PV": 5,
      "Outros": 999
    };

    return [...order.items].sort((a, b) => {
      const segmentA = mapEtapaToSegment(a.productSeries);
      const segmentB = mapEtapaToSegment(b.productSeries);

      const weightA = segmentOrder[segmentA] || 999;
      const weightB = segmentOrder[segmentB] || 999;

      if (weightA !== weightB) return weightA - weightB;

      const seriesA = mapEtapaToGradeName(a.productSeries);
      const seriesB = mapEtapaToGradeName(b.productSeries);
      return seriesA.localeCompare(seriesB);
    });
  }, [order?.items]);

  // Replaced LoadingState with OrderDetailsSkeleton
  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  if (isError || !order) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Pedido não encontrado. Verifique o número do pedido e tente novamente.
            </AlertDescription>
          </Alert>
          <Button variant="outline" onClick={handleGoBack} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Voltar para Meus Pedidos
          </Button>
        </div>
      </div>
    );
  }

  // Garante que `order.items` seja um array para evitar erros e aplica a ordenação.
  const orderItems = sortedOrderItems;
  // Cupom por itens (itens com unidades cobertas): o total dos itens já vem com o desconto.
  const hasItemCoupon = orderItems.some((item) => (item.couponQuantity ?? 0) > 0);

  // Ver .claude/docs/frontend_order_cancellation.md, seções 1 e 3.1 — pedido já enviado ao sistema
  // externo continua elegível: a solicitação abre (ou reaproveita) um chamado no Zendesk. Só é
  // recusada quando nem isso faz sentido (Em Rota/Entregue/Cancelado, mesmo critério que o backend usa
  // em `SkipTicketForInvoiceStatuses` pra decidir não abrir ticket) — a seção avisa o motivo em modal.
  // Cancelamento/ticket já registrados são tratados pela própria seção.
  const cancellationBlockedReason =
    order.isScheduled ? 'Pedidos programados não podem ser cancelados por aqui.'
      : statusOrder === 'Em Rota' ? 'O pedido já saiu para entrega.'
        : statusOrder === 'Entregue' ? 'O pedido já foi entregue.'
          : statusOrder === 'Cancelado' ? 'O pedido já está cancelado.'
            : null;
  const hasExternalStatus = (orderStatus?.statusHistory?.length ?? 0) > 0;
  // Programados (aberto pela visão PROGRAMADO ou pedido gerador `scheduled_source`, 2027+) exibem itens de
  // scheduled_orders_items, que o POST de troca/checklist não aceita.
  const isScheduledView = location.state?.typePedido === 'PROGRAMADO';
  const isScheduledOrder = isScheduledView || !!order.isScheduled || order.status === 'scheduled_source';
  // Troca, checklist e demais solicitações por item: só com o pedido entregue e nunca cancelado.
  const itemRequestBlockedReason = getItemRequestBlockedReason({
    statusLabel: getStatusLabel(statusOrder),
    cancelledAt: order.cancelledAt,
    isScheduledOrder,
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <Breadcrumb className="mb-4">
            <BreadcrumbList aria-label="Navegação de trilha">
              <BreadcrumbItem><BreadcrumbLink asChild><Link to={pageConf.home} aria-label="Página inicial"><Home className="h-4 w-4" aria-hidden="true" /></Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink asChild><Link to={pageConf.toBack}>Pedidos</Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>{order.orderNumber}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">#{order.orderNumber}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                {isLoadingOrderStatus ? (
                  <Skeleton className="h-6 w-24" />
                ) : (
                  <StateBadge status={statusOrder} />
                )}
                {hasOverdueInstallments(installments) && (
                  <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" aria-hidden="true" />Parcelas Atrasadas</Badge>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleGoBack}><ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />Voltar</Button>
              <Button
                onClick={handleDownloadPDF}
                disabled={downloadPDF}
              >
                {downloadPDF ? (
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-4 w-4" aria-hidden="true" />
                )}
                Baixar PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className={SECTION_TITLE_CLASS}>
                <Building2 className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />Dados do Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-sm text-muted-foreground">Nome da Escola</div>
                <div className="font-medium">{order.clientName}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Código do Cliente</div>
                <div className="font-medium">{order.clientCode}</div>
              </div>
              {order.customer?.clientCnpj && (
                <div>
                  <div className="text-sm text-muted-foreground">CNPJ</div>
                  <div className="font-medium">{order.customer.clientCnpj}</div>
                </div>
              )}
              {order.customer?.clientCustomerGroup && (
                <div>
                  <div className="text-sm text-muted-foreground">Grupo do Cliente</div>
                  <div className="font-medium">{order.customer.clientCustomerGroup}</div>
                </div>
              )}
              {order.clientEmail && (
                <div>
                  <div className="text-sm text-muted-foreground">Email</div>
                  <div className="font-medium">{order.clientEmail}</div>
                </div>
              )}
              {order.clientPhone && (
                <div>
                  <div className="text-sm text-muted-foreground">Telefone</div>
                  <div className="font-medium">{order.clientPhone}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className={SECTION_TITLE_CLASS}>
                <Package className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />Informações do Pedido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" aria-hidden="true" />
                <div>
                <div className="text-sm text-muted-foreground">Data de Entrega</div>
                <div className="font-medium">{formatDate( new Date( order.deliveryDate ) )}</div>
                </div>
                </div> */}
              <div className="grid grid-cols-2 gap-2">
                <div className="gap-2 flex flex-col">
                  <div>
                    <div className="text-sm text-muted-foreground">Status do Pedido</div>
                    <div className="mt-1">
                      {isLoadingOrderStatus ? (
                        <Skeleton className="h-6 w-24" />
                      ) : (
                        <StateBadge status={statusOrder} />
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Data do Pedido</div>
                    <div className="font-medium">{formatDate(new Date(order.createdAt!))}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Frete</div>
                    <div className="mt-1">{formatCurrency(order.shippingAmount)}</div>
                  </div>
                  {order.userEmail && (
                    <div>
                      <div className="text-sm text-muted-foreground">Criado por</div>
                      <div className="mt-1">{order.userEmail}</div>
                    </div>
                  )}
                </div>
                <div className="gap-2 flex flex-col">
                  {order.previsionDelivery && (
                    <div>
                      <div className="text-sm text-muted-foreground">Previsão de Entrega</div>
                      <div className="mt-1">{formatDate(new Date(order.previsionDelivery))}</div>
                    </div>
                  )}
                  {order.deliveryDate && (
                    <div>
                      <div className="text-sm text-muted-foreground">Data de Entrega</div>
                      <div className="mt-1">{formatDate(new Date(order.deliveryDate))}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* <div>
                <div className="text-sm text-muted-foreground">Situação Financeira</div>
                <div className="mt-1">
                  {hasOverdueInstallments( installments ) ? <Badge variant="destructive">Atrasado</Badge> : <Badge variant="default">Em Dia</Badge>}
                </div>
              </div> */}
            </CardContent>
          </Card>
        </div>

        {isCustomerGroupAddressEnabled && <OrderAddresses order={order} />}

        {/* Timeline do Status */}
        <Card>
          <CardHeader>
            <CardTitle className={SECTION_TITLE_CLASS}>
              <Activity className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />Status do Pedido
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingOrderStatus ? (
              <div className="flex items-center justify-between">
                {[1, 2, 3, 4].map((i) => (
                  <React.Fragment key={i}>
                    <div className="flex flex-col items-center flex-1">
                      <Skeleton className="h-9 w-9 rounded-full mb-2" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    {i < 4 && <div className="h-0.5 flex-1 bg-muted mx-2" />}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-center flex-1" aria-label={`Status Criado, ${statusOrder !== "Cancelado" ? "completo" : "inativo"}`}>
                  <div className={cn("rounded-full p-2 mb-2", statusOrder !== "Cancelado" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className={cn("text-xs text-center", statusOrder !== "Cancelado" && "font-semibold text-primary")}>Criado</div>
                </div>
                <div className={cn("h-0.5 flex-1", ['Processado', 'Em Rota', "Entregue"].includes(statusOrder) ? "bg-primary" : "bg-muted")} aria-hidden="true" />
                <div className="flex flex-col items-center flex-1" aria-label={`Status Processando, ${['Processado', 'Entregue', 'Em Rota'].includes(statusOrder) ? "completo" : "pendente"}`}>
                  <div className={cn("rounded-full p-2 mb-2", ['Processado', 'Em Rota', "Entregue"].includes(statusOrder) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    <Clock className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className={cn("text-xs text-center", ['Processado', 'Em Rota', "Entregue"].includes(statusOrder) && "font-semibold text-primary")}>Processados</div>
                </div>

                <div className={cn("h-0.5 flex-1", ['Em Rota', "Entregue"].includes(statusOrder) ? "bg-primary" : "bg-muted")} aria-hidden="true" />
                <div className="flex flex-col items-center flex-1" aria-label={`Status Processando, ${['Em Rota', "Entregue"].includes(statusOrder) ? "completo" : "pendente"}`}>
                  <div className={cn("rounded-full p-2 mb-2", ['Em Rota', "Entregue"].includes(statusOrder) ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    <TruckIcon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className={cn("text-xs text-center", ['Em Rota', "Entregue"].includes(statusOrder) && "font-semibold text-primary")}>Em Rota</div>
                </div>

                <div className={cn("h-0.5 flex-1", statusOrder === "Entregue" ? "bg-primary" : "bg-muted")} aria-hidden="true" />
                <div className="flex flex-col items-center flex-1" aria-label={`Status Entregue, ${statusOrder === "Entregue" ? "completo" : "pendente"}`}>
                  <div className={cn("rounded-full p-2 mb-2", statusOrder === "Entregue" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                    <Package className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className={cn("text-xs text-center", statusOrder === "Entregue" && "font-semibold text-primary")}>Entregue</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <OrderCancellationSection
          orderId={orderId!}
          order={order}
          orderQueryKey={orderDetailsQueryKey}
          isEligibilityPending={isLoadingOrderStatus}
          blockedReason={cancellationBlockedReason}
          hasExternalStatus={hasExternalStatus}
          orderStatusLabel={getStatusLabel(statusOrder)}
          onStartNewOrder={() => navigate(pageConf.newOrder)}
          items={orderItems}
          getItemUsage={(item) => getTypeDelivery({ productCode: String(item.productCode), productSku: String(item.productSku) })}
          itemRequestBlockedReason={itemRequestBlockedReason}
          canLoadRequests={!isScheduledView}
        />

        {/* {order.comment && (
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" aria-hidden="true" />Observações</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-muted-foreground">{order.comment}</p></CardContent>
          </Card>
        )} */}

        <Card>
          <CardHeader>
            <CardTitle className={SECTION_TITLE_CLASS}><FileBarChart className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />Produtos do Pedido</CardTitle>
            <CardDescription>{orderItems.length} {orderItems.length === 1 ? 'item' : 'itens'} no pedido</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="">
              <Table>
                <caption className="sr-only">Detalhes dos produtos incluídos no pedido {order.orderNumber}</caption>
                <TableHeader>
                  <TableRow>
                    {/* <TableHead scope="col">Código</TableHead> */}
                    <TableHead scope="col">Descrição</TableHead>
                    <TableHead scope="col" className="text-left">Segmento</TableHead>
                    <TableHead scope="col" className="text-left">Série</TableHead>
                    <TableHead scope="col" className="text-left">Tipo</TableHead>
                    <TableHead scope="col" className="text-left">SKU</TableHead>
                    <TableHead scope="col" className="text-left">Quantidade</TableHead>
                    <TableHead scope="col" className="text-left">Valor Unit.</TableHead>
                    <TableHead scope="col" className="text-left">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {splitOrderItemRows(orderItems).map(({ item, kind, quantity, total }, index) => (
                    <TableRow key={`${item.productCode}-${index}`} className={kind === "coupon" ? "bg-emerald-50/60" : undefined}>
                      {/* <TableCell className="font-mono text-sm">{item.productCode}</TableCell> */}
                      <TableCell>
                        {item.productDescription}
                        {kind === "coupon" && (
                          <Badge variant="outline" className="ml-2 border-emerald-300 text-emerald-700">
                            {order.couponCode ? `Cupom ${order.couponCode}` : "Cupom"}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-left">{mapEtapaToSegment(item.productSeries)}</TableCell>
                      <TableCell className="text-left">{mapEtapaToGradeName(item.productSeries)}</TableCell>
                      <TableCell className="text-left">{getTypeDelivery({ productCode: String(item.productCode), productSku: String(item.productSku) })}</TableCell>
                      <TableCell className="text-left">{item.productSku}</TableCell>

                      <TableCell className="text-left">{quantity}</TableCell>
                      <TableCell className="text-left">{formatCurrency(item.unitPrice)}</TableCell>
                      <TableCell className="text-left font-medium">
                        {formatCurrency(total)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(order.shippingAmount > 0 || (order.couponDiscountAmount ?? 0) > 0) && (
                    // Cupom por itens: o total dos itens já vem líquido; sem linha de desconto, o subtotal é a soma deles.
                    // Cupom antigo (valor/percentual): mantém subtotal bruto + linha de desconto.
                    <>
                      <TableRow>
                        <TableCell colSpan={6} ></TableCell>
                        <TableCell className="text-left font-semibold">Subtotal</TableCell>
                        <TableCell className="text-left font-medium">
                          {formatCurrency(order.totalAmount + (hasItemCoupon ? 0 : (order.couponDiscountAmount ?? 0)))}
                        </TableCell>
                      </TableRow>
                      {(order.couponDiscountAmount ?? 0) > 0 && !hasItemCoupon && (
                        <TableRow>
                          <TableCell colSpan={6} ></TableCell>
                          <TableCell className="text-left font-semibold">
                            Desconto do cupom{order.couponCode ? ` (${order.couponCode})` : ""}
                          </TableCell>
                          <TableCell className="text-left font-medium">
                            - {formatCurrency(order.couponDiscountAmount ?? 0)}
                          </TableCell>
                        </TableRow>
                      )}
                      {order.shippingAmount > 0 && (
                        <TableRow>
                          <TableCell colSpan={6} ></TableCell>
                          <TableCell className="text-left font-semibold">Frete</TableCell>
                          <TableCell className="text-left font-medium">{formatCurrency(order.shippingAmount)}</TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                  <TableRow className="bg-muted/50">
                    <TableCell colSpan={6} ></TableCell>
                    <TableCell className="text-left font-semibold">Total do Pedido</TableCell>
                    <TableCell className="text-left font-bold text-lg">{formatCurrency(order.totalAmount + order.shippingAmount)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <OrderInvoices
          isLoading={isLoadingOV || isLoadingBoleto}
          invoices={orderInvoices}
        />

        {/* Últimas Interações */}
        {role === 'Administrator' &&
          <Card className="border-border dark:border-border shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={SECTION_TITLE_CLASS}>
                  <MessageSquare className={SECTION_TITLE_ICON_CLASS} aria-hidden="true" />
                  Últimas Interações
                </CardTitle>
              </div>
              <CardDescription>Acompanhe o histórico de status do seu pedido.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingOrderStatus ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3 items-start p-3">
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-4 w-1/3" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : orderStatus?.statusHistory && orderStatus.statusHistory.length > 0 ? (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {orderStatus.statusHistory.map((activity) => (
                      <div key={activity.createdAt} className="flex gap-3 items-start hover:bg-muted/50 p-3 rounded-md transition-colors">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{activity.portalB2bOrderStatus || 'Pendente'}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateTime(new Date(activity.createdAt))}
                            {role === 'Administrator' && (
                              <>
                                {' '}
                                • {activity.purchaseOrdersStatusTypeName}
                                {' '}
                                • {activity.externalApiStatus}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex h-[300px] flex-col items-center justify-center rounded-md border border-dashed bg-muted/50 p-8 text-center">
                  <MessageSquare className="h-10 w-10 text-muted-foreground" />
                  <p className="mt-4 font-medium">Nenhuma interação registrada</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Quando houver atualizações sobre o status do pedido, elas aparecerão aqui.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        }

        {/* <div className="pt-4"><h2 className="text-xl font-bold mb-4">Informações Financeiras</h2></div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency( financialSummary.totalAmount )}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Pago</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency( financialSummary.totalPaid )}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {installments.filter( i => i.status === "paid" ).length} parcela(s) paga(s)
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Pendente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{formatCurrency( financialSummary.totalPending )}</div>
              <p className="text-xs text-muted-foreground mt-1">{installments.filter( i => i.status !== "paid" ).length} parcela(s) pendente(s)</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" aria-hidden="true" />Parcelas do Pedido</CardTitle>
            <CardDescription>Histórico de pagamento e próximos vencimentos</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Parcela</TableHead>
                  <TableHead scope="col">Vencimento</TableHead>
                  <TableHead scope="col" className="text-right">Valor</TableHead>
                  <TableHead scope="col">Status</TableHead>
                  <TableHead scope="col">Pagamento</TableHead>
                  <TableHead scope="col" className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installments.map( ( installment ) => (
                  <TableRow key={installment.installmentId} className={installment.status === "overdue" ? "bg-destructive/5" : ""}>
                    <TableCell className="font-medium">{installment.installmentNumber}/{installments.length}</TableCell>
                    <TableCell>{formatDate( new Date( installment.dueDate ) )}</TableCell>
                    <TableCell className="text-right">{formatCurrency( installment.amount )}</TableCell>
                    <TableCell>{getInstallmentStatusBadge( installment.status )}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{installment.paymentDate ? formatDate( new Date( installment.paymentDate ) ) : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {installment.status !== "paid" && ( <Button variant="outline" size="sm" onClick={() => handleDownloadBoleto( installment )}><Receipt className="h-4 w-4 mr-1" aria-hidden="true" />Boleto</Button> )}
                        {installment.paymentProofUrl && ( <Button variant="ghost" size="sm" onClick={() => handleDownloadProof( installment )}><FileText className="h-4 w-4 mr-1" aria-hidden="true" />Comprovante</Button> )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) )}
              </TableBody>
            </Table>
          </CardContent>
        </Card> */}
      </main >
    </div >
  );
};

export default OrderDetails;